import { Badge, Popover, Tabs } from "antd";
import type { PopoverProps } from 'antd';
import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import CommonIcons from "../../assets/icons";
import
{
  markAllAsRead,
  markAsRead,
} from "../../store/reducers/notificationSlice";
import useNotificationPolling from "./useNotificationPolling";
import { useNavigate } from "react-router-dom";

export default function NotificationPanel({
  onClose,
}: {
  onClose?: () => void;
})
{
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const notifications = useSelector((state: RootState) => state.notificationSlice.total) || [];

  // Start HTTP polling when component mounts (fallback)
  // useNotificationPolling();

  const unreadCount = notifications.filter((n) => !n.read).length;
  const [activeTab, setActiveTab] = React.useState<string>("all");
  const [detailOpenId, setDetailOpenId] = React.useState<string | number | null>(null);

  const handleMarkAllAsRead = () =>
  {
    dispatch(markAllAsRead());
  };


  const filteredNotifications =
    activeTab === "all" ? notifications : notifications.filter((n) => !n.read);

  const tabItems = [
    {
      key: "all",
      label: t("tabs.all"),
    },
    {
      key: "unread",
      label: t("tabs.unread"),
    },
  ];

  const handleScroll = React.useCallback(() =>
  {
    setDetailOpenId(null);
  }, []);

  return (
    <div className="w-[380px] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-3 pb-0">
        <h3 className="m-0 text-base font-semibold text-[var(--color-text-primary)]">
          {t("notifications.title")}
        </h3>
      </div>

      {/* Tabs */}
      <div
        className="flex justify-between items-center"
        style={{ borderBottom: "1px solid #61687E" }}
      >
        <Tabs
          activeKey={activeTab}
          items={tabItems}
          onChange={setActiveTab}
          className="notification-tabs"
          tabBarStyle={{ marginBottom: -1 }}
          size="small"
        />
        {activeTab === "unread" && unreadCount > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            className="text-xs text-blue-500 hover:text-blue-400 transition-colors flex items-center gap-1"
          >
            <CommonIcons.MarkReadAllIcon />
            {t("markAllRead")}
          </button>
        )}
      </div>

      {/* Notification List */}
      <div className="px-2 py-3">
        {filteredNotifications.length === 0 ? (
          <div className="text-center text-gray-400 py-8">
            {t("noNotifications")}
          </div>
        ) : (
          <div className="overflow-y-auto max-h-[350px] custom-scrollbar" onScroll={handleScroll} >
            {filteredNotifications.map((notification) =>
            {

              const isOpen = detailOpenId === notification.id;

              const detailContent = (
                <div className="notification-detail-content p-3">
                  <h4 className="font-semibold mb-2 text-[var(--color-text-primary)]">
                    {notification.title}
                  </h4>
                  {notification.message && (
                    <p className="text-sm text-[var(--color-text-secondary)] mb-2">
                      {notification.message}
                    </p>
                  )}
                  <div className="text-xs text-[var(--color-text-tertiary)]">
                    {moment(notification.createAt).format("HH:mm DD/MM/YYYY")}
                  </div>

                </div>
              );

              return (
                <Popover
                  key={notification.id}
                  overlayClassName="popover-detail"
                  open={isOpen}
                  onOpenChange={(newOpen) =>
                  {
                    if (!newOpen)
                    {
                      setDetailOpenId(null);
                    } else
                    {
                      dispatch(markAsRead(notification.id));
                      setDetailOpenId(notification.id);
                    }
                  }}
                  trigger="click"
                  placement="right"
                  getPopupContainer={(triggerNode) => document.body}
                  content={detailContent}
                  showArrow={false}
                >
                  <div
                    className={`p-3 mb-2 rounded cursor-pointer transition-colors ${notification.read
                      ? "bg-[var(--color-bg-container)]"
                      : "bg-[var(--neutral-900)]"
                      }`}

                    onClick={() =>
                    {
                      const path = window.location.pathname;

                      console.log(notification, 'notification');
                      console.log(path, 'path');
                      if (notification?.tags && notification?.tags?.length > 0 && !notification?.tags?.includes(path))
                      {
                        // If notification has a link and it's different from current path, show as unread
                        navigate(`/${notification.tags[0]}`);
                        dispatch(markAsRead(notification.id));
                        return null; // Skip rendering this notification in the list
                      }
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 pr-2">
                        <p
                          className={`mb-1 ${notification.read
                            ? "text-[var(--color-text-secondary)]"
                            : "font-semibold text-[var(--color-text-primary)]"
                            }`}
                        >
                          {notification.title}
                        </p>
                        <p className="text-xs text-[var(--color-text-tertiary)]">
                          {moment(notification.createAt).format("HH:mm DD/MM/YYYY")}
                        </p>
                      </div>
                      {!notification.read && (
                        <Badge dot status="processing" className="mt-1" />
                      )}
                    </div>
                  </div>
                </Popover>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
